import * as THREE from "three";
import { LineMaterial } from "three/examples/jsm/lines/LineMaterial.js";
import { LineSegments2 } from "three/examples/jsm/lines/LineSegments2.js";
import { LineSegmentsGeometry } from "three/examples/jsm/lines/LineSegmentsGeometry.js";
import { isPointInsideFurnitureXZ } from "../../utils/geometry.js";

export class FurnitureDebugOverlay {
  /**
   * Toggle a visual overlay showing every module-accepting AP in the scene.
   *  - Green  = unoccupied (available for snapping)
   *  - Red    = occupied   (blocked)
   * Press D to toggle. The overlay refreshes live during drags.
   */
  debugToggleModuleAPs() {
    if (this._debugAPsActive) {
      this.furniture.forEach(f => {
        f.attachmentPoints.forEach(ap => {
          if (ap.acceptsComponentTypes.includes('module')) ap.hideDebug();
        });
      });
      this._debugDisposeCollisionBoxHelpers();
      this._debugCollisionBoxHelpers = [];
      this._debugAPsActive = false;
    } else {
      this._debugAPsActive = true;
      this._debugRefreshModuleAPs();
    }
  }

  /** Refresh overlay colours — called after every drop and every drag-move tick.
   *  Red    = formally occupied (attachedTo set)
   *  Orange = unoccupied but physically blocked by an adjacent module's AABB
   *  Green  = truly free
   */
  _debugRefreshModuleAPs() {
    if (!this._debugAPsActive) return;
    const modules = this.furniture.filter(f => f.furnitureData?.componentType === 'module');

    this.furniture.forEach(f => {
      f.attachmentPoints.forEach(ap => {
        if (!ap.acceptsComponentTypes.includes('module')) return;

        let color;
        if (ap.isOccupied) {
          color = '#ff4444'; // red — formally occupied
        } else {
          const wp = ap.getWorldPosition();
          const blocked = modules.some(other =>
            other !== f && isPointInsideFurnitureXZ(other, wp.x, wp.z, 0.05)
          );
          color = blocked ? '#ff9900' : '#44ff44'; // orange vs green
        }

        ap.showDebug(color);
      });
    });

    this._debugRefreshCollisionBoxes();
  }

  _debugDisposeCollisionBoxHelpers() {
    this._debugCollisionBoxHelpers.forEach(h => {
      h.parent?.remove(h);
      h.geometry.dispose();
      h.material.dispose();
    });
  }

  _debugRefreshCollisionBoxes() {
    this._debugDisposeCollisionBoxHelpers();
    this._debugCollisionBoxHelpers = [];

    const size = new THREE.Vector2();
    this.renderer.getSize(size);

    const makeFatBox = (sx, sy, sz) => {
      const hx = sx / 2, hy = sy / 2, hz = sz / 2;
      const c = [
        [-hx, -hy, -hz], [ hx, -hy, -hz], [ hx,  hy, -hz], [-hx,  hy, -hz],
        [-hx, -hy,  hz], [ hx, -hy,  hz], [ hx,  hy,  hz], [-hx,  hy,  hz],
      ];
      const edges = [
        [0,1],[1,2],[2,3],[3,0],
        [4,5],[5,6],[6,7],[7,4],
        [0,4],[1,5],[2,6],[3,7],
      ];
      const positions = [];
      for (const [a, b] of edges) positions.push(...c[a], ...c[b]);
      const geom = new LineSegmentsGeometry();
      geom.setPositions(positions);
      const mat = new LineMaterial({ color: 0x00ffff, linewidth: 3, depthTest: false, resolution: size });
      return new LineSegments2(geom, mat);
    };

    this.furniture.forEach(f => {
      if (!f.boundingBox || !f.mesh) return;

      if (f.collisionBoxes?.length > 0) {
        f.collisionBoxes.forEach(cb => {
          const line = makeFatBox(cb.size.x, cb.size.y, cb.size.z);
          line.position.set(cb.offset.x, cb.offset.y, cb.offset.z);
          f.mesh.add(line);
          this._debugCollisionBoxHelpers.push(line);
        });
      } else {
        const bb = f.boundingBox;
        const line = makeFatBox(bb.size.x, bb.size.y, bb.size.z);
        line.position.copy(bb.localCenterOffset);
        f.mesh.add(line);
        this._debugCollisionBoxHelpers.push(line);
      }
    });
  }
}
